import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAcl, type SystemRole } from '@/hooks/useAcl';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { isRoleHomeRoute } from '@/router/roleRoutes';
import Badge from '@/components/base/Badge';
import type { AclFeature } from '@/mocks/aclPermissions';

type BadgeVariant = 'primary' | 'accent' | 'secondary';

const ROLE_BADGE_VARIANTS: Readonly<Record<string, BadgeVariant>> = {
  'Super Admin': 'primary',
  'Admin':       'primary',
  'Coordinator': 'accent',
  'Teacher':     'secondary',
  'Therapist':   'accent',
};

const FALLBACK_BADGE_VARIANT: BadgeVariant = 'secondary';

function getRoleBadgeVariant(role: SystemRole): BadgeVariant {
  return ROLE_BADGE_VARIANTS[role] ?? FALLBACK_BADGE_VARIANT;
}

/** 'Super Admin' -> 'login.roleSuperAdmin'; falls back to the raw role when untranslated. */
function getRoleLabelKey(role: SystemRole): string {
  return `login.role${role.replace(/\s+/g, '')}`;
}

interface NavChild {
  labelKey: string;
  path: string;
}

interface NavItem {
  labelKey: string;
  icon: string;
  path: string;
  badge?: { label: string; variant: 'primary' | 'accent' | 'secondary' | 'error' };
  feature: AclFeature;
  /** Pinned below the divider at the foot of the nav, regardless of ACL filtering. */
  bottomAnchored?: boolean;
  /** The role's landing page; `/dashboard` prefix-matches everything, so match it explicitly. */
  homeItem?: boolean;
  children?: NavChild[];
}

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

const NAV_ITEMS: NavItem[] = [
  { labelKey: 'common.dashboard',       icon: 'ri-dashboard-3-line',    path: '/dashboard',        feature: 'dashboard', homeItem: true },
  { labelKey: 'students.pageTitle',         icon: 'ri-team-line',           path: '/dashboard/admin/students',         feature: 'students' },
  { labelKey: 'teachers.pageTitle',       icon: 'ri-user-settings-line',  path: '/dashboard/admin/teachers',            feature: 'teachers' },
  { labelKey: 'dashboard.studentsAttendance',    icon: 'ri-group-line',          path: '/dashboard/admin/attendance',         feature: 'students' },
  { labelKey: 'dashboard.staffAttendance', icon: 'ri-calendar-schedule-line', path: '/dashboard/admin/staff-attendance',   feature: 'scheduling' },
  { labelKey: 'dashboard.schoolCalendar', icon: 'ri-calendar-2-line',    path: '/dashboard/admin/school-calendar', feature: 'schoolCalendar' },
  { labelKey: 'dashboard.announcements', icon: 'ri-megaphone-line',  path: '/announcements',   feature: 'announcements' },
  { labelKey: 'lessonPlans.listPageTitle', icon: 'ri-file-list-3-line', path: '/dashboard/teacher-dashboard/lesson-plans', feature: 'lessonPlan' },
  { labelKey: 'dashboard.reports',    icon: 'ri-bar-chart-grouped-line', path: '/reports',       feature: 'reports' },
  { labelKey: 'dashboard.accessControl', icon: 'ri-shield-check-line', path: '/dashboard/admin/access-control', feature: 'accessControl', bottomAnchored: true },
  {
    labelKey: 'common.settings',
    icon: 'ri-settings-4-line',
    path: '/settings',
    feature: 'settings',
    bottomAnchored: true,
    children: [
      { labelKey: 'settings.general', path: '/dashboard/admin/settings' },
      { labelKey: 'settings.classMaster', path: '/dashboard/admin/settings?tab=classes' },
      { labelKey: 'settings.rolesMaster', path: '/dashboard/admin/settings?tab=roles' },
      { labelKey: 'settings.sections', path: '/dashboard/admin/settings?tab=sections' },
    ],
  },
];

export default function Sidebar({ collapsed = false, onToggle }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { role, canView } = useAcl();
  const [mobileOpen, setMobileOpen] = useState(false);

  const currentUser = useCurrentUser();
  const roleLabel = t(getRoleLabelKey(role), role);

  // ── ACL-driven sidebar filtering ──
  const filteredItems = NAV_ITEMS.filter((item) => canView(item.feature));
  const mainItems = filteredItems.filter((item) => !item.bottomAnchored);
  const bottomItems = filteredItems.filter((item) => item.bottomAnchored);

  const isActive = (path: string) => {
    if (path.includes('?')) {
      const [pathname, search] = path.split('?');
      return location.pathname.startsWith(pathname) && location.search.includes(search.split('=')[1]);
    }
    return location.pathname.startsWith(path);
  };

  const isParentActive = (item: NavItem) => {
    if (item.homeItem) {
      return isRoleHomeRoute(location.pathname);
    }
    if (item.children) {
      return item.children.some((child) => {
        if (child.path.includes('?')) {
          const [childPathname] = child.path.split('?');
          return location.pathname.startsWith(childPathname);
        }
        return location.pathname.startsWith(child.path);
      });
    }
    return location.pathname.startsWith(item.path);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const renderNavItem = (item: NavItem, collapsedLocal: boolean) => {
    const hasChildren = !!item.children && item.children.length > 0;
    const expanded = hasChildren && isParentActive(item);

    return (
      <div key={item.path}>
        <button
          onClick={() => handleNavigate(item.children?.[0]?.path ?? item.path)}
          className={`nav-item w-full text-left ${isParentActive(item) ? 'active' : ''} ${collapsedLocal ? 'justify-center px-0' : ''}`}
          title={collapsedLocal ? t(item.labelKey, item.labelKey) : undefined}
        >
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className={`${item.icon} text-base`} />
          </span>
          {!collapsedLocal && (
            <>
              <span className="flex-1 text-left">{t(item.labelKey, item.labelKey)}</span>
              {item.badge && (
                <Badge variant={item.badge.variant} size="sm">{item.badge.label}</Badge>
              )}
              {hasChildren && (
                <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs text-foreground-400 transition-transform`} />
              )}
            </>
          )}
        </button>

        {hasChildren && expanded && !collapsedLocal && (
          <div className="ml-6 mt-0.5 mb-0.5 flex flex-col gap-0.5 border-l-2 border-foreground-700/30 pl-2">
            {item.children!.map((child) => (
              <button
                key={child.path}
                onClick={() => handleNavigate(child.path)}
                className={`nav-item w-full text-left text-xs py-1.5 ${isActive(child.path) ? 'active' : ''}`}
              >
                <span className="flex-1 text-left">{t(child.labelKey, child.labelKey)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const SidebarContent = () => (
    <aside
      className={`
        flex flex-col h-full
        bg-foreground-900
        transition-all duration-250
        ${collapsed ? 'w-[68px]' : 'w-[240px]'}
      `}
    >
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-foreground-700/40 flex-shrink-0 ${collapsed ? 'justify-center px-2' : ''}`}>
        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center">
          <img
            src="https://storage.readdy-site.link/project_files/56cd5c38-7b0a-4a14-b4db-95b4319f943c/bf902dd9-b2ed-4d83-8680-96eb5154116a_compressed_XL_Logo.webp"
            alt="Xceptional Learning"
            className="w-8 h-8 object-contain"
          />
        </div>
        {!collapsed && (
          <div>
            <p className="font-heading font-700 text-foreground-50 text-sm leading-tight">Xceptional</p>
            <p className="font-heading font-700 text-primary-400 text-sm leading-tight">Learning</p>
          </div>
        )}
      </div>

      {!collapsed && (
        <div className="px-4 py-3 border-b border-foreground-700/40 flex-shrink-0">
          <Badge variant={getRoleBadgeVariant(role)} size="sm" dot>
            {roleLabel}
          </Badge>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-2 py-4" aria-label="Main navigation">
        <div className="flex flex-col gap-0.5">
          {mainItems.map((item) => renderNavItem(item, collapsed))}
        </div>

        {!collapsed && bottomItems.length > 0 && (
          <div className="mt-4 pt-4 border-t border-foreground-700/30 flex flex-col gap-0.5">
            {bottomItems.map((item) => renderNavItem(item, collapsed))}
          </div>
        )}
      </nav>

      <div className={`px-3 py-4 border-t border-foreground-700/40 flex-shrink-0 ${collapsed ? 'flex justify-center' : 'flex items-center gap-3'}`}>
        <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-heading font-700 cursor-pointer">
          {currentUser.initials}
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <p className="text-foreground-100 text-xs font-medium font-label truncate">{currentUser.name}</p>
            <p className="text-foreground-500 text-xs font-label truncate">{currentUser.email}</p>
          </div>
        )}
        {!collapsed && (
          <button
            aria-label="Sign out"
            title="Sign out"
            className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-200 hover:bg-foreground-700/50 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-logout-box-r-line text-sm" />
          </button>
        )}
      </div>

      {onToggle && (
        <button
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={`
            absolute top-1/2 -right-3 -translate-y-1/2
            w-6 h-6 flex items-center justify-center
            bg-foreground-800 border border-foreground-700 rounded-full
            text-foreground-400 hover:text-foreground-100
            transition-colors cursor-pointer
            hidden lg:flex
          `}
        >
          <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-sm`} />
        </button>
      )}
    </aside>
  );

  return (
    <>
      <div className="hidden lg:block relative h-full flex-shrink-0">
        <SidebarContent />
      </div>

      <button
        onClick={() => setMobileOpen(true)}
        aria-label="Open navigation"
        className="lg:hidden fixed top-4 left-4 z-40 w-10 h-10 flex items-center justify-center bg-foreground-900 text-foreground-200 rounded-xl shadow-raised cursor-pointer"
      >
        <i className="ri-menu-line text-lg" />
      </button>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="relative h-full">
            <SidebarContent />
          </div>
        </div>
      )}
    </>
  );
}